import './recipient-form.css';

import React, { useCallback, useEffect, useState } from 'react';
import SkeletonLoader from 'tiny-skeleton-loader-react';

import usePostAddress from '../../../api/addresses/usePostAddress';
import useUpdateAddress from '../../../api/addresses/useUpdateAddress';
import useGetRequest from '../../../api/useGetRequest';
import GenericModal from '../../../components/agv-generic-modal';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../../enums/modal-size.enum';
import {
  COMMUNICATION_PREFERENCES,
  EMPTY_PHONE_NUMBER,
  EMPTY_RECIPIENT,
  isEmailFormatValid,
  isImportedRecipient,
  PHONE_NUMBER_TYPES,
  toRecipientFormState,
  toRecipientPayload,
} from './recipient-form.utils';
import RecipientLocationMap from './recipient-location-map';
import useAddressSuggestions, {
  toAddressFields,
} from './use-address-suggestions';

/**
 * Champ du formulaire : libellé (avec mention « requis »), saisie, indice dessous
 */
const RfField = ({ label, required, hint, wide, children }) => (
  <label className={`rf-field ${wide ? 'rf-field--wide' : ''}`}>
    <span className="rf-field__label">
      {label}
      {required && <span className="rf-required">requis</span>}
    </span>
    {children}
    {hint && <span className="rf-field__hint">{hint}</span>}
  </label>
);

const RfSection = ({ title, description, children }) => (
  <section className="rf-section">
    <div className="rf-section__head">
      <div>
        <h3 className="rf-section__title">{title}</h3>
        {description && (
          <p className="rf-section__description">{description}</p>
        )}
      </div>
    </div>
    {children}
  </section>
);

/**
 * Modale de création ou de modification d'un destinataire
 * @param {boolean} isOpen
 * @param {Function} setIsOpen
 * @param {number|null} addressId - Destinataire à modifier (null : création)
 * @param {Function} onSaved - Appelé avec le destinataire enregistré (détail renvoyé par l'API) ;
 * il ferme la modale. Sans onSaved, la modale se ferme d'elle-même
 */
export default function RecipientFormModal({
  isOpen,
  setIsOpen,
  addressId,
  onSaved,
}) {
  const isEdition = Boolean(addressId);
  const [formState, setFormState] = useState(EMPTY_RECIPIENT);
  const [addressQuery, setAddressQuery] = useState('');

  const { data: recipient, isLoading: isLoadingRecipient } = useGetRequest(
    isOpen && isEdition ? `/addresses/${addressId}` : null,
    0
  );
  const { suggestions, isSearching, clearSuggestions } =
    useAddressSuggestions(addressQuery);
  const { isLoading: isCreating, postAddress } = usePostAddress();
  const { isLoading: isUpdating, updateAddress } = useUpdateAddress();

  // Tant que le détail chargé n'est pas celui du destinataire demandé, le formulaire attend
  const editedRecipient =
    isEdition && recipient?.id === addressId ? recipient : null;
  const isImported = isImportedRecipient(editedRecipient);
  const isFormReady = !isEdition || Boolean(editedRecipient);

  // Formulaire réinitialisé à chaque ouverture, prérempli en modification
  useEffect(() => {
    if (isOpen) {
      setFormState(toRecipientFormState(editedRecipient));
      setAddressQuery('');
    }
  }, [isOpen, editedRecipient]);

  const update = (field, value) =>
    setFormState((previous) => ({ ...previous, [field]: value }));

  const updateCoordinates = useCallback(
    (latitude, longitude) =>
      setFormState((previous) => ({
        ...previous,
        latitude: Number(latitude.toFixed(6)),
        longitude: Number(longitude.toFixed(6)),
      })),
    []
  );

  const handleSelectSuggestion = (suggestion) => {
    const addressFields = toAddressFields(suggestion);
    setFormState((previous) =>
      isImported
        ? // Destinataire importé : seule la position est reprise de la suggestion
          {
            ...previous,
            latitude: addressFields.latitude,
            longitude: addressFields.longitude,
          }
        : { ...previous, ...addressFields }
    );
    setAddressQuery('');
    clearSuggestions();
  };

  const updatePhoneNumber = (index, field, value) =>
    setFormState((previous) => ({
      ...previous,
      phone_numbers: previous.phone_numbers.map((phoneNumber, i) =>
        i === index ? { ...phoneNumber, [field]: value } : phoneNumber
      ),
    }));

  const removePhoneNumber = (index) =>
    setFormState((previous) => ({
      ...previous,
      phone_numbers: previous.phone_numbers.filter((_, i) => i !== index),
    }));

  const updateComment = (index, value) =>
    setFormState((previous) => ({
      ...previous,
      comments: previous.comments.map((comment, i) =>
        i === index ? value : comment
      ),
    }));

  const removeComment = (index) =>
    setFormState((previous) => ({
      ...previous,
      comments: previous.comments.filter((_, i) => i !== index),
    }));

  const handleClose = () => {
    setIsOpen(false);
    setAddressQuery('');
    clearSuggestions();
  };

  const handleSubmit = async () => {
    const payload = toRecipientPayload(formState, editedRecipient);

    try {
      const savedRecipient = isEdition
        ? await updateAddress(addressId, payload)
        : await postAddress(payload);
      setAddressQuery('');
      clearSuggestions();
      // onSaved se charge de fermer la modale (et d'enchaîner, par exemple, sur le détail)
      if (onSaved) {
        onSaved(savedRecipient);
      } else {
        setIsOpen(false);
      }
    } catch (error) {
      // Message affiché par le hook, la modale reste ouverte pour corriger la saisie
      console.error("Erreur lors de l'enregistrement du destinataire:", error);
    }
  };

  const isEmailValid = isEmailFormatValid(formState.email);
  const isFormValid =
    isFormReady && formState.name.trim() !== '' && isEmailValid;
  const hasCoordinates =
    Number.isFinite(formState.latitude) && Number.isFinite(formState.longitude);
  const phoneTypeOptions = (phoneNumberType) =>
    PHONE_NUMBER_TYPES.includes(phoneNumberType)
      ? PHONE_NUMBER_TYPES
      : [phoneNumberType, ...PHONE_NUMBER_TYPES];

  const renderContent = () => {
    if (!isFormReady) {
      return (
        <div className="rf">
          {[...Array(3)].map((_, index) => (
            <SkeletonLoader
              key={index}
              width="100%"
              height={120}
              style={{ borderRadius: 8 }}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="rf">
        <RfSection
          title="Identification"
          description="Ce qui permet de reconnaître le destinataire dans les tournées et les imports."
        >
          {isImported && (
            <p className="rf-info">
              Destinataire issu d&apos;un import : son nom et son adresse
              restent ceux du système d&apos;origine. Vous pouvez ajuster sa
              position, ses contacts et ses commentaires.
            </p>
          )}
          <div className="rf-grid">
            <RfField label="Nom" required>
              <input
                className="rf-input"
                value={formState.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="Ex : Boulangerie du Marché"
                disabled={isImported}
                autoFocus={!isEdition}
              />
            </RfField>
            <RfField
              label="Code destinataire"
              hint={
                isEdition
                  ? 'Non modifiable'
                  : 'Facultatif · généré automatiquement (TMS-…) si vide'
              }
            >
              <input
                className="rf-input"
                value={formState.address_key}
                onChange={(e) => update('address_key', e.target.value)}
                placeholder="Ex : CLI-0042"
                disabled={isEdition}
              />
            </RfField>
          </div>
        </RfSection>

        <RfSection
          title="Adresse"
          description="Recherchez l'adresse puis ajustez au besoin le point de livraison en déplaçant le repère sur la carte."
        >
          <div className="rf-geocoder">
            <span className="rf-geocoder__icon">⌕</span>
            <input
              className="rf-input rf-input--search"
              value={addressQuery}
              onChange={(e) => setAddressQuery(e.target.value)}
              placeholder={
                isImported
                  ? 'Rechercher une adresse pour repositionner le destinataire…'
                  : 'Rechercher une adresse…'
              }
              aria-label="Rechercher une adresse"
            />
            {isSearching && <span className="rf-spinner" />}
            {suggestions.length > 0 && (
              <div className="rf-geocoder__results">
                {suggestions.map((suggestion) => (
                  <button
                    type="button"
                    key={suggestion.place_id}
                    className="rf-geocoder__result"
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    {suggestion.display_name}
                  </button>
                ))}
              </div>
            )}
            {addressQuery.trim().length > 2 &&
              !isSearching &&
              suggestions.length === 0 && (
                <span className="rf-field__hint">
                  Aucune adresse trouvée — saisissez-la ci-dessous et placez le
                  repère en cliquant sur la carte.
                </span>
              )}
          </div>

          <div className="rf-grid">
            <RfField label="Rue" wide>
              <input
                className="rf-input"
                value={formState.street}
                onChange={(e) => update('street', e.target.value)}
                placeholder="Ex : 12 rue de la Paix"
                disabled={isImported}
              />
            </RfField>
            <RfField
              label="Complément d'adresse"
              hint="Bâtiment, quai, digicode…"
              wide
            >
              <input
                className="rf-input"
                value={formState.address_complement}
                onChange={(e) => update('address_complement', e.target.value)}
                placeholder="Ex : Entrée par la cour, quai n°3"
              />
            </RfField>
            <RfField label="Code postal">
              <input
                className="rf-input"
                value={formState.zipcode}
                onChange={(e) => update('zipcode', e.target.value)}
                placeholder="Ex : 75002"
                disabled={isImported}
              />
            </RfField>
            <RfField label="Ville">
              <input
                className="rf-input"
                value={formState.city}
                onChange={(e) => update('city', e.target.value)}
                placeholder="Ex : Paris"
                disabled={isImported}
              />
            </RfField>
            <RfField label="Pays" hint="Code pays, ex : FR">
              <input
                className="rf-input"
                value={formState.country}
                onChange={(e) => update('country', e.target.value)}
                placeholder="FR"
                disabled={isImported}
              />
            </RfField>
          </div>

          <RecipientLocationMap
            latitude={formState.latitude}
            longitude={formState.longitude}
            onChange={updateCoordinates}
          />
          {hasCoordinates ? (
            <div className="rf-map__meta">
              <span>
                Position : {formState.latitude.toFixed(5)},{' '}
                {formState.longitude.toFixed(5)}
              </span>
              <button
                type="button"
                className="rf-link"
                onClick={() =>
                  setFormState((previous) => ({
                    ...previous,
                    latitude: null,
                    longitude: null,
                  }))
                }
              >
                Retirer la position
              </button>
            </div>
          ) : (
            <p className="rf-warning">
              Sans position, le destinataire ne pourra pas être placé sur la
              carte ni pris en compte par l&apos;optimisation des tournées.
              Choisissez une adresse ou cliquez sur la carte.
            </p>
          )}
        </RfSection>

        <RfSection
          title="Contact"
          description="Coordonnées utilisées par les chauffeurs et pour les notifications de livraison."
        >
          <div className="rf-grid">
            <RfField label="Email">
              <input
                className={`rf-input ${
                  isEmailValid ? '' : 'rf-input--invalid'
                }`}
                type="email"
                value={formState.email}
                onChange={(e) => update('email', e.target.value)}
                placeholder="contact@exemple.fr"
              />
            </RfField>
            <RfField label="Préférence de communication">
              <select
                className="rf-input"
                value={formState.communication_preference}
                onChange={(e) =>
                  update('communication_preference', e.target.value)
                }
              >
                {COMMUNICATION_PREFERENCES.map((preference) => (
                  <option key={preference.value} value={preference.value}>
                    {preference.label}
                  </option>
                ))}
              </select>
            </RfField>
          </div>

          <div className="rf-field rf-field--wide">
            <span className="rf-field__label">Téléphones</span>
            {formState.phone_numbers.length > 0 ? (
              <div className="rf-table">
                <div className="rf-table__row rf-table__row--head">
                  <span className="rf-col--type">Type</span>
                  <span className="rf-col--grow">Contact</span>
                  <span className="rf-col--phone">Numéro</span>
                  <span className="rf-col--remove" />
                </div>
                {formState.phone_numbers.map((phoneNumber, index) => (
                  <div className="rf-table__row" key={index}>
                    <span className="rf-col--type">
                      <select
                        className="rf-input"
                        value={phoneNumber.name}
                        onChange={(e) =>
                          updatePhoneNumber(index, 'name', e.target.value)
                        }
                        aria-label="Type de numéro"
                      >
                        {phoneTypeOptions(phoneNumber.name).map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </span>
                    <span className="rf-col--grow">
                      <input
                        className="rf-input"
                        value={phoneNumber.fullname}
                        onChange={(e) =>
                          updatePhoneNumber(index, 'fullname', e.target.value)
                        }
                        placeholder="Facultatif"
                        aria-label="Nom du contact"
                      />
                    </span>
                    <span className="rf-col--phone">
                      <input
                        className="rf-input"
                        type="tel"
                        value={phoneNumber.phone_number}
                        onChange={(e) =>
                          updatePhoneNumber(
                            index,
                            'phone_number',
                            e.target.value
                          )
                        }
                        placeholder="06 12 34 56 78"
                        aria-label="Numéro de téléphone"
                      />
                    </span>
                    <span className="rf-col--remove">
                      <button
                        type="button"
                        className="rf-remove"
                        onClick={() => removePhoneNumber(index)}
                        aria-label="Retirer ce numéro"
                        title="Retirer ce numéro"
                      >
                        ✕
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="rf-empty">Aucun numéro enregistré</span>
            )}
            <button
              type="button"
              className="rf-link"
              onClick={() =>
                update('phone_numbers', [
                  ...formState.phone_numbers,
                  EMPTY_PHONE_NUMBER,
                ])
              }
            >
              + Ajouter un numéro
            </button>
          </div>
        </RfSection>

        <RfSection
          title="Commentaires"
          description="Consignes de livraison transmises aux chauffeurs (accès, horaires, interlocuteur…)."
        >
          {formState.comments.map((comment, index) => (
            <div className="rf-comment" key={index}>
              <textarea
                className="rf-input rf-textarea rf-col--grow"
                value={comment}
                onChange={(e) => updateComment(index, e.target.value)}
                placeholder="Ex : Livrer par l'entrée de service avant 7h"
                aria-label={`Commentaire ${index + 1}`}
              />
              <span className="rf-col--remove">
                <button
                  type="button"
                  className="rf-remove"
                  onClick={() => removeComment(index)}
                  aria-label="Retirer ce commentaire"
                  title="Retirer ce commentaire"
                >
                  ✕
                </button>
              </span>
            </div>
          ))}
          <button
            type="button"
            className="rf-link"
            onClick={() => update('comments', [...formState.comments, ''])}
          >
            + Ajouter un commentaire
          </button>
        </RfSection>
      </div>
    );
  };

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title={isEdition ? 'Modifier le destinataire' : 'Créer un destinataire'}
      mainAction={handleSubmit}
      mainActionText={
        isEdition ? 'Enregistrer les modifications' : 'Créer le destinataire'
      }
      mainActionDisabled={!isFormValid || (isEdition && isLoadingRecipient)}
      mainActionIsLoading={isCreating || isUpdating}
      secondaryAction={handleClose}
      secondaryActionText="Annuler"
      size={MODAL_SIZE_STYLE[MODAL_SIZE.XL]}
    >
      {renderContent()}
    </GenericModal>
  );
}
